import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { DepositBloc } from '@connect-services/client/blocs/deposit.bloc';
import { RouteCompletePathEnum } from '@connect-services/client/models/routes.model';
import { DepositValidationErrorsComponent } from '@connect-services/client/pages/management/data/deposit/deposit-validation-errors/deposit-validation-errors.component';
import { CreateDepositLayerService } from '@connect-services/client/services/deposit/layer/create-deposit-layer.service';
import { ExpansionPanelComponent } from '@connect-services/src/lib/components/expansion-panel/expansion-panel.component';
import { Deposit, DepositsService, DepositValidation } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DepositValidationErrorsComponent, ExpansionPanelComponent, RessourcesModule],
    selector: 'app-deposit-validation',
    templateUrl: './deposit-validation.component.html',
})
export class DepositValidationComponent {
    public readonly deposit = input.required<Deposit | undefined>();

    protected readonly columnErrors = computed<string[]>(() => {
        const columns = this.depositValidation()?.errors.columns ?? [];

        return [...columns]
            .sort((columnErrorA, columnErrorB) => {
                if (columnErrorA.table === columnErrorB.table) {
                    return columnErrorA.column > columnErrorB.column ? 1 : -1;
                }

                return columnErrorA.table > columnErrorB.table ? 1 : -1;
            })
            .map((columnError) => {
                return `${columnError.table}: ${columnError.column}`;
            });
    });
    protected readonly depositValidation = computed<DepositValidation | null>(() => {
        return this.deposit()?.depositValidation ?? null;
    });
    protected readonly depositValidationInProgressStatuses: Deposit.StatusEnum[] = [
        Deposit.StatusEnum.EXTRACTING,
        Deposit.StatusEnum.IMPORTING,
        Deposit.StatusEnum.VALIDATING,
    ];
    protected readonly hasValidationErrors = computed<boolean>(() => {
        return !!this.tableErrors().length || !!this.columnErrors().length || !!this.tableDuplicates().length;
    });
    protected readonly importErrorMessage = computed<string | null>(() => {
        const importError = this.depositValidation()?.errors.importError;
        if (!importError) {
            return null;
        }

        const language = this.activeLanguage().split('-')[0];

        if (language === 'en') {
            return importError.en || importError.fr;
        }

        return importError.fr || importError.en;
    });
    protected readonly isDepositValidationInProgress = computed<boolean>(() => {
        const status = this.deposit()?.status;
        if (status == null) {
            return false;
        }

        return this.depositValidationInProgressStatuses.includes(status);
    });
    protected readonly tableDuplicates = computed<string[]>(() => {
        return this.depositValidation()?.errors.duplicateTables ?? [];
    });
    protected readonly tableErrors = computed<string[]>(() => {
        return this.depositValidation()?.errors.tables ?? [];
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly createDepositLayerService = inject(CreateDepositLayerService);
    protected readonly Deposit = Deposit;

    private readonly depositBloc = inject(DepositBloc);
    private readonly depositsService = inject(DepositsService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly activeLanguage = toSignal(this.translocoService.langChanges$, {
        initialValue: this.translocoService.getActiveLang(),
    });

    public async relaunchValidation(): Promise<void> {
        const deposit = this.deposit();

        if (!deposit) {
            return;
        }

        await firstValueFrom(
            this.depositsService.depositControllerValidate({
                id: deposit.id,
            }),
        );

        this.snackBarService.openSuccessSnackBar('depositValidateInProgress');

        await this.depositBloc.load({
            id: deposit.id,
        });

        await this.router.navigate(RouteCompletePathEnum.ORGANIZATION_DATA);
    }
}
